import React from 'react';
import { Briefcase, ShoppingBag, Cloud, Truck, HeartPulse, CheckCircle2, ArrowRight, Sparkles } from 'lucide-react';

interface IndustriesPageProps {
  onNavigate: (path: string) => void;
  onStartProject: () => void;
  onOpenConsultation: () => void;
}

export const IndustriesPage: React.FC<IndustriesPageProps> = ({
  onNavigate,
  onStartProject,
  onOpenConsultation,
}) => {
  const industries = [
    {
      id: 'professional-services',
      title: 'Professional Services & Consulting',
      icon: Briefcase,
      color: '#159DE8',
      challenges: 'Outdated corporate websites failing to project high-level authority, low conversion from enterprise search, and manual booking bottlenecks.',
      solution: 'Prestigious corporate web platforms with interactive ROI calculators, streamlined consultation funnels, and high-authority B2B SEO.',
      deliverables: ['Custom B2B Corporate Website', 'Executive Bio & Case Study System', 'Automated Lead Qualification Funnels', 'Technical On-Page SEO']
    },
    {
      id: 'ecommerce',
      title: 'E-Commerce & Digital Retail',
      icon: ShoppingBag,
      color: '#FF851B',
      challenges: 'Slow page loads causing high cart abandonment, unoptimized mobile checkouts, catalog sync errors, and low Return on Ad Spend (ROAS).',
      solution: 'Headless storefronts (Next.js / Shopify) with sub-second transitions, Google Shopping (PMax) feed integration, and dynamic retargeting.',
      deliverables: ['Headless Storefront Engineering', 'Google Merchant Center Optimization', 'Cart Abandonment Recovery Workflows', 'Performance Max Ad Campaigns']
    },
    {
      id: 'saas-startups',
      title: 'SaaS Companies & Tech Startups',
      icon: Cloud,
      color: '#7B2EFF',
      challenges: 'Hasty MVP technical debt, slow feature velocity, multi-tenant database scaling issues, and weak organic buyer search presence.',
      solution: 'Production-ready cloud architectures with row-level security, Stripe usage-based billing, automated onboarding, and programmatic SEO.',
      deliverables: ['Multi-Tenant Cloud SaaS MVP', 'Billing & Metering Integration', 'Interactive User Onboarding', 'High-Intent Comparison SEO Pages']
    },
    {
      id: 'logistics',
      title: 'Logistics & Supply Chain Management',
      icon: Truck,
      color: '#00C853',
      challenges: 'Fragmented field communication, manual dispatch spreadsheets, delayed inventory reconciliation, and poor mobile network connectivity.',
      solution: 'Offline-first hybrid mobile applications with real-time GPS telemetry, cloud ERP syncing, and unified operational executive dashboards.',
      deliverables: ['Cross-Platform Field Driver App', 'Real-Time Inventory Cloud Sync', 'Automated Dispatch Workflows', 'Role-Based Executive Dashboards']
    },
    {
      id: 'healthcare',
      title: 'Healthcare & Wellness Practices',
      icon: HeartPulse,
      color: '#FF5A36',
      challenges: 'Patient privacy concerns, clunky appointment scheduling, weak local search visibility, and poor mobile usability for patients.',
      solution: 'Secure, accessible web platforms featuring encrypted patient portals, seamless calendar integrations, and local Google Business Profile dominance.',
      deliverables: ['Secure Patient Intake Portal', 'Online Consultation Scheduling', 'Local Medical SEO & Schema Markup', 'Strict Data Privacy Compliance']
    }
  ];

  return (
    <div className="bg-[#071426] text-[#D5DFEC] py-12 space-y-16 min-h-screen">
      
      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0B2342] border border-[#1b2f4a] text-xs font-semibold uppercase tracking-wider text-[#FF851B]">
            <Sparkles size={14} className="text-[#159DE8]" />
            <span>INDUSTRY SPECIALIZATIONS</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white font-display tracking-tight leading-tight">
            Digital Engineering Built for Your Industry's Commercial Reality
          </h1>

          <p className="text-base sm:text-lg text-[#D5DFEC] leading-relaxed">
            We don't build generic websites. We design and engineer targeted digital ecosystems tailored to the exact commercial workflows, user expectations, and regulatory requirements of your industry.
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {industries.map((ind) => {
            const Icon = ind.icon;
            return (
              <div
                key={ind.id}
                className="p-8 rounded-3xl bg-[#0c1a2d] border border-[#1b2f4a] hover:border-[#159DE8]/50 transition-all duration-300 shadow-card-dark flex flex-col justify-between group"
              >
                <div className="space-y-5">
                  <div className="flex items-center justify-between">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center border shadow-md"
                      style={{ backgroundColor: `${ind.color}15`, borderColor: `${ind.color}40`, color: ind.color }}
                    >
                      <Icon size={22} />
                    </div>
                    <span className="text-[11px] font-mono text-[#8FA0B8] uppercase tracking-wider">
                      Targeted Sector
                    </span>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white font-display group-hover:text-[#FF851B] transition-colors">
                      {ind.title}
                    </h2>
                  </div>

                  <div className="space-y-2 p-4 rounded-xl bg-[#050A13] border border-[#1b2f4a] text-xs">
                    <div className="text-red-400 font-bold uppercase tracking-wider text-[10px]">Commercial Challenge:</div>
                    <p className="text-[#8FA0B8] leading-relaxed">{ind.challenges}</p>
                  </div>

                  <div className="space-y-2 p-4 rounded-xl bg-[#071426] border border-[#1b2f4a] text-xs">
                    <div className="text-[#00C853] font-bold uppercase tracking-wider text-[10px]">The EBON Solution:</div>
                    <p className="text-[#D5DFEC] leading-relaxed">{ind.solution}</p>
                  </div>

                  <div className="space-y-2 pt-2">
                    <span className="text-xs uppercase tracking-wider font-semibold text-white block">Key Deliverables:</span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {ind.deliverables.map((del, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-[#8FA0B8]">
                          <CheckCircle2 size={13} className="text-[#159DE8] shrink-0" />
                          <span>{del}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pt-6 mt-6 border-t border-[#1b2f4a] flex items-center justify-between">
                  <button
                    onClick={onStartProject}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#FF851B] group-hover:text-white transition-colors"
                  >
                    <span>Request Sector Proposal</span>
                    <ArrowRight size={13} />
                  </button>
                  <span className="text-[11px] text-[#8FA0B8]">US, UK & Canada SLA</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Box */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-r from-[#0B2342] to-[#071426] border border-[#1b2f4a] flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-white font-display">Operate in another specialized industry?</h2>
            <p className="text-sm text-[#D5DFEC]">Schedule a discovery session with our lead architects to review your technical requirements.</p>
          </div>
          <button
            onClick={onOpenConsultation}
            className="px-8 py-3.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-sm font-bold shadow-lg hover:shadow-glow-orange transition-all shrink-0 flex items-center gap-2"
          >
            <span>Book a Consultation</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

    </div>
  );
};
